"use client";

import type { ComponentProps, ReactNode } from "react";
import type {
  LeadFlowDocument,
  LeadFlowStepDefinition,
  LeadFlowSubmission,
} from "../types";
import type { getStepLayout } from "../utils/get-step-layout";

import { cn } from "@homeinstead/ui";
import { ViewTransition } from "react";
import {
  TAKEOVER_QUESTION_COLUMN_GRID_CLASS_NAME,
  TAKEOVER_QUESTION_COLUMN_STEP_CLASS_NAME,
} from "../constants/takeover-class-names";
import { getPanelTransitionName } from "../utils/get-panel-transition-name";
import { LeadFlowStepProvider } from "./LeadFlowStepContext";
import { Progress } from "./Progress";
import { Step } from "./Step";
import { SubmissionError } from "./SubmissionError";
import { ThankYou } from "./ThankYou";

/**
 * Row gap only: at `md` this element is also the question-column grid, and
 * a column gap would sit inside that column.
 */
const TAKEOVER_STEP_GAP_CLASS_NAME = "gap-y-8 md:gap-y-7";

type StepFrameProps = {
  blockKey: string;
  currentStep: LeadFlowStepDefinition;
  hasServerError: boolean;
  /** Section content leading the in-page first step. */
  header?: ReactNode;
  isLastStep: boolean;
  isReady: boolean;
  isShowingThankYou: boolean;
  isSubmitPending: boolean;
  isSubmitting: boolean;
  isTakingOverSection: boolean;
  layout: ReturnType<typeof getStepLayout>;
  onBack: (() => void) | null;
  stepContext: ComponentProps<typeof LeadFlowStepProvider>["value"];
  submission: LeadFlowSubmission | null;
  thankYou: LeadFlowDocument["thankYou"];
  title: string;
  transitionClassName: string;
  /** Changes with each step, so the view transition runs between them. */
  transitionKey: string;
};

/**
 * The open flow's current step or thank-you screen, under its progress
 * indicator. Progress stays outside the view transition so it updates in
 * place while the step swaps.
 */
function StepFrame({
  blockKey,
  currentStep,
  hasServerError,
  header,
  isLastStep,
  isReady,
  isShowingThankYou,
  isSubmitPending,
  isSubmitting,
  isTakingOverSection,
  layout,
  onBack,
  stepContext,
  submission,
  thankYou,
  title,
  transitionClassName,
  transitionKey,
}: StepFrameProps) {
  const { sharesQuestionColumn } = layout;

  return (
    <div
      className={cn(
        "flex flex-col",
        isTakingOverSection ? TAKEOVER_STEP_GAP_CLASS_NAME : "gap-6",
        sharesQuestionColumn && TAKEOVER_QUESTION_COLUMN_GRID_CLASS_NAME,
      )}
    >
      {layout.showProgress ? (
        <Progress
          indicator={layout.progressIndicator}
          onBack={onBack}
          position={layout.progress.position}
          total={layout.progress.total}
        />
      ) : null}
      <ViewTransition
        default="none"
        enter={transitionClassName}
        exit={transitionClassName}
        key={transitionKey}
      >
        {isShowingThankYou && submission ? (
          <ThankYou submittedAnswers={submission.answers} thankYou={thankYou} />
        ) : (
          <div
            className={
              sharesQuestionColumn
                ? TAKEOVER_QUESTION_COLUMN_STEP_CLASS_NAME
                : undefined
            }
          >
            <LeadFlowStepProvider value={stepContext}>
              <Step
                canShowPanel={layout.canShowPanel}
                header={header}
                isLastStep={isLastStep}
                isReady={isReady}
                isSubmitPending={isSubmitPending}
                isSubmitting={isSubmitting}
                onBack={layout.stepShowsBack ? onBack : null}
                panelTransitionClassName={transitionClassName}
                panelTransitionName={getPanelTransitionName(
                  blockKey,
                  currentStep.id,
                )}
                sharesQuestionColumn={sharesQuestionColumn}
                step={currentStep}
                title={title}
              />
            </LeadFlowStepProvider>
            {hasServerError ? (
              <div
                className={sharesQuestionColumn ? "md:col-span-2" : undefined}
              >
                <SubmissionError />
              </div>
            ) : null}
          </div>
        )}
      </ViewTransition>
    </div>
  );
}

export { StepFrame };
